import { ModalContentCollection } from './ModalContentCollection';
import { ModalContentState } from './ModalContentState';
import type { ModalContentItem } from './types';

/**
 * // Class Obj: モーダルに表示する汎用コンテンツの移動先を管理
 * // Note:
 * // - このクラスは「次にどの item を表示するか」を計算するだけです。
 * // - 実際の描画は ModalContentRenderer、現在状態の更新は ModalContentState が担当します。
 */
export class ModalContentNavigator
{
  private readonly mContentCollection: ModalContentCollection;
  private readonly mContentState: ModalContentState;

  constructor(content_collection: ModalContentCollection, content_state: ModalContentState)
  {
    this.mContentCollection = content_collection;
    this.mContentState = content_state;
  }

  /**
   * // Method: 同一 group 内の次の item を取得する
   */
  public getNextItem(is_loop_enabled: boolean = false): ModalContentItem | null
  {
    return this.getMovedItem(1, is_loop_enabled);
  }

  /**
   * // Method: 同一 group 内の前の item を取得する
   */
  public getPrevItem(is_loop_enabled: boolean = false): ModalContentItem | null
  {
    return this.getMovedItem(-1, is_loop_enabled);
  }

  /**
   * // Method: 次の group の item を取得する
   */
  public getNextGroup(is_loop_enabled: boolean = false): ModalContentItem | null
  {
    return this.getMovedGroupItem(1, is_loop_enabled);
  }

  /**
   * // Method: 前の group の item を取得する
   */
  public getPrevGroup(is_loop_enabled: boolean = false): ModalContentItem | null
  {
    return this.getMovedGroupItem(-1, is_loop_enabled);
  }

  /**
   * // Method: 同一 group 内で offset 分移動した item を取得する
   * // Note:
   * // - is_loop_enabled が true の場合、先頭 / 末尾で同一 group 内を折り返します。
   * // - item が1件しかない場合は、ループ有効でも移動先なしとして null を返します。
   */
  private getMovedItem(
    offset: number,
    is_loop_enabled: boolean = false,
  ): ModalContentItem | null
  {
    const current_item: ModalContentItem | null = this.mContentState.getCurrentItem();

    if (!current_item)
    {
      return null;
    }

    const group_items: ModalContentItem[] = this.mContentCollection.getItemsByGroup(
      current_item.group_name,
    );

    const current_position: number = group_items.findIndex((item: ModalContentItem) =>
    {
      return item.item_index === current_item.item_index;
    });

    if (current_position < 0)
    {
      return null;
    }

    if (group_items.length <= 1)
    {
      return null;
    }

    const moved_position: number | null = this.resolveMovedPosition(
      current_position,
      offset,
      group_items.length,
      is_loop_enabled,
    );

    if (moved_position === null)
    {
      return null;
    }

    return group_items[moved_position] ?? null;
  }

  /**
   * // Method: group 単位で offset 分移動した item を取得する
   * // Note:
   * // - group を移動した場合は、移動先 group の先頭 item を返します。
   * // - item_index は引き継がず、group 移動時に 0 番目相当へ戻します。
   * // - is_loop_enabled が true の場合、先頭 / 末尾 group で折り返します。
   */
  private getMovedGroupItem(
    offset: number,
    is_loop_enabled: boolean = false,
  ): ModalContentItem | null
  {
    const current_item: ModalContentItem | null = this.mContentState.getCurrentItem();

    if (!current_item)
    {
      return null;
    }

    const group_names: string[] = this.mContentCollection.getGroupNames();

    const current_group_position: number = group_names.findIndex((group_name: string) =>
    {
      return group_name === current_item.group_name;
    });

    if (current_group_position < 0)
    {
      return null;
    }

    if (group_names.length <= 1)
    {
      return null;
    }

    const moved_group_position: number | null = this.resolveMovedPosition(
      current_group_position,
      offset,
      group_names.length,
      is_loop_enabled,
    );

    if (moved_group_position === null)
    {
      return null;
    }

    const moved_group_name: string | undefined = group_names[moved_group_position];

    if (!moved_group_name)
    {
      return null;
    }

    const moved_group_items: ModalContentItem[] = this.mContentCollection.getItemsByGroup(
      moved_group_name,
    );

    return moved_group_items[0] ?? null;
  }

  /**
   * // Method: 移動後の配列位置を解決する
   * // Note:
   * // - ループ無効時は範囲外なら null を返します。
   * // - ループ有効時は、負方向 / 正方向どちらでも配列内へ折り返します。
   */
  private resolveMovedPosition(
    current_position: number,
    offset: number,
    total_count: number,
    is_loop_enabled: boolean,
  ): number | null
  {
    const moved_position: number = current_position + offset;

    if (moved_position >= 0 && moved_position < total_count)
    {
      return moved_position;
    }

    if (!is_loop_enabled)
    {
      return null;
    }

    return (moved_position + total_count) % total_count;
  }
}
